import React from "react";
import {
  Container,
  Stack,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  Box,
  Avatar,
  Divider,
  Button,
  alpha,
  useTheme,
  Link,
} from "@mui/material";
import {
  Storefront,
  LocationOn,
  Phone,
  Email,
  ChevronRight,
} from "@mui/icons-material";
import { motion } from "framer-motion";

export default function BranchesSct({ branches = [], isLoading = false }) {
  const theme = useTheme();

  // Branches list mapping
  const branchesList = branches?.map((branch, index) => (
    <Grid size={{ xs: 12, md: 6 }} key={branch?.code || index}>
      <motion.div
        initial={{ opacity: 0, x: -15 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: index * 0.04 }}
        whileHover={{ y: -2 }}
        style={{ height: "100%" }}
      >
        <Card
          sx={{
            height: "100%",
            borderRadius: 1,
            overflow: "hidden",
            border: `1px solid ${alpha(theme?.palette?.divider, 0.08)}`,
            transition: "all 0.25s",
            "&:hover": {
              boxShadow: "0 8px 30px rgba(0,0,0,0.06)",
              borderColor: theme?.palette?.primary?.main,
            },
          }}
        >
          <Box
            sx={{
              height: 3,
              background: `linear-gradient(90deg, ${theme?.palette?.primary?.main}, ${theme?.palette?.secondary?.main})`,
            }}
          />

          <CardContent sx={{ p: 3 }}>
            <Stack direction="row" gap={1.5} alignItems="center" mb={1.5}>
              <Avatar
                sx={{
                  bgcolor: alpha(theme?.palette?.primary?.main, 0.08),
                  color: theme?.palette?.primary?.main,
                  width: 40,
                  height: 40,
                  borderRadius: 1,
                }}
              >
                <Storefront sx={{ fontSize: 20 }} />
              </Avatar>
              <Box flex={1}>
                <Typography variant="h6" fontWeight={700} fontSize="1rem">
                  {branch?.title}
                </Typography>
                {branch?.location && (
                  <Chip
                    label={branch?.location}
                    size="small"
                    sx={{
                      bgcolor: alpha(theme?.palette?.info?.main, 0.08),
                      color: theme?.palette?.info?.main,
                      fontWeight: 500,
                      fontSize: "0.65rem",
                      height: 20,
                      mt: 0.3,
                    }}
                  />
                )}
              </Box>
            </Stack>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mb: 2,
                lineHeight: 1.7,
                textAlign: "justify",
                fontSize: "0.85rem",
                minHeight: 40,
              }}
            >
              {branch?.desc || "توضیحات این شعبه..."}
            </Typography>

            <Divider sx={{ my: 1.5 }} />

            <Stack gap={1}>
              {branch?.address && (
                <Stack direction="row" gap={1} alignItems="center">
                  <LocationOn
                    sx={{
                      fontSize: 16,
                      color: theme?.palette?.primary?.main,
                    }}
                  />
                  <Typography variant="body2" fontSize="0.8rem">
                    {branch?.address}
                  </Typography>
                </Stack>
              )}
              {branch?.phone_number?.[0] && (
                <Stack direction="row" gap={1} alignItems="center">
                  <Phone
                    sx={{
                      fontSize: 16,
                      color: theme?.palette?.primary?.main,
                    }}
                  />
                  <Link
                    href={`tel:${branch?.phone_number?.[0]}`}
                    variant="body2"
                    dir="ltr"
                    fontSize="0.8rem"
                    sx={{ textDecoration: "none", color: "text.primary" }}
                  >
                    {branch?.phone_number?.[0]}
                  </Link>
                </Stack>
              )}
              {branch?.email?.[0] && (
                <Stack direction="row" gap={1} alignItems="center">
                  <Email
                    sx={{
                      fontSize: 16,
                      color: theme?.palette?.primary?.main,
                    }}
                  />
                  <Typography variant="body2" fontSize="0.8rem">
                    {branch?.email?.[0]}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      </motion.div>
    </Grid>
  ));

  return (
    <Container maxWidth="xl" sx={{ py: 6 }}>
      {!isLoading && branches?.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Stack direction="row" gap={2} alignItems="center" sx={{ mb: 4 }}>
            <Storefront
              sx={{ fontSize: 28, color: theme?.palette?.primary?.main }}
            />
            <Typography variant="h4" fontWeight={700} fontSize="1.6rem">
              شعب ما
            </Typography>
            <Chip
              label={`${branches?.length || 0} شعبه`}
              color="primary"
              size="small"
              sx={{ fontWeight: 600, fontSize: "0.7rem", px: 1.5 }}
            />
          </Stack>

          <Grid container spacing={3}>
            {branchesList}
          </Grid>
        </motion.div>
      )}
    </Container>
  );
}
